import { Component, JSX, h, Prop, Method, getAssetPath, ComponentInterface } from '@stencil/core';
import { SessionStorageKeys } from '@aglenergy/shared-library';
import { ErrorMessage } from 'app/shared/models/errorModal.model';

@Component({
  tag: 'error-modal',
  styleUrl: 'error-modal.scss'
})
export class ErrorModal implements ComponentInterface {
  @Prop() public errorMessageData: ErrorMessage;

  private aglDsModalRef: any;

  @Method()
  public async showModal(elToBeFocused: HTMLElement): Promise<void> {
    await this.aglDsModalRef.openModal(elToBeFocused);
  }

  @Method()
  public async closeModal(): Promise<void> {
    await this.aglDsModalRef.closeModal();
  }

  private handleModalClosed(forceRefresh: boolean, redirectToMovers = false): void {
    if (this.errorMessageData.errorTitle === 'Unable to connect you') {
      sessionStorage.removeItem(SessionStorageKeys.ProductDetailsSession);
    }
    if (redirectToMovers) {
      this.clearNbnMoversStoredData();
      window.location.href = '/apps/confirm/?#/movers/address';
    } else if (forceRefresh) {
      window.location.href = this.getWindowLocationWithoutAddressParam();
    }
  }

  private clearNbnMoversStoredData(): void {
    let customerProfile = JSON.parse(sessionStorage.getItem('customerProfileService'));
    let questionResponseMovers = JSON.parse(sessionStorage.getItem('questionResponseMovers'));

    customerProfile = {
      ...customerProfile,
      isGasUpdated: false,
      isElectricityUpdated: false,
      isInternetUpdated: false,
      isMobileUpdated: false
    };
    if (questionResponseMovers) {
      questionResponseMovers = questionResponseMovers.filter((question) => question.name === 'movers-new-existing');
    }

    sessionStorage.setItem('customerProfileService', JSON.stringify(customerProfile));
    sessionStorage.setItem('questionResponseMovers', JSON.stringify(questionResponseMovers));
  }

  private getWindowLocationWithoutAddressParam() {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('address');
    searchParams.delete('locid');
    let newPath = window.location.pathname;
    if (searchParams.toString()) {
      newPath += `?${searchParams.toString()}`;
    }
    return newPath;
  }

  private renderContact(): JSX.Element {
    if (this.errorMessageData.contactType === 'redirect') {
      return (
        <agl-ds-p class="error-modal__contact">
          If you have any questions,{' '}
          <agl-ds-link
            styledAs="md"
            appearance="default"
            openNewWindow={true}
            href={this.errorMessageData.contactUrl}
            srContext="Contact us"
          >
            contact us
          </agl-ds-link>
          .
        </agl-ds-p>
      );
    } else if (this.errorMessageData.contactType === 'tryAgainOrRedirect') {
      return (
        <agl-ds-p class="error-modal__contact">
          Please try again or{' '}
          <agl-ds-link
            styledAs="md"
            appearance="default"
            openNewWindow={true}
            href={this.errorMessageData.contactUrl}
            srContext="Contact us"
          >
            contact us
          </agl-ds-link>
          .
        </agl-ds-p>
      );
    } else if (this.errorMessageData.contactType === 'getInTouch') {
      return <agl-ds-p class="error-modal__contact">If you have any questions, please get in touch with us.</agl-ds-p>;
    } else if (this.errorMessageData.contactType === 'moversContactUs') {
      return (
        <agl-ds-p class="error-modal__contact">
          If you have any questions, please{' '}
          <agl-ds-link
            styledAs="md"
            appearance="default"
            openNewWindow={true}
            href={this.errorMessageData.contactUrl}
            srContext="Contact us"
          >
            contact us
          </agl-ds-link>
          .
        </agl-ds-p>
      );
    }
    return null;
  }

  private renderMessage(): JSX.Element {
    if (this.errorMessageData.messageUrl && this.errorMessageData.messageLinkText) {
      return (
        <agl-ds-p class="error-modal__message">
          {this.errorMessageData.errorMessage}
          <agl-ds-link
            appearance="default"
            styled-as="md"
            open-new-window="false"
            href={this.errorMessageData.messageUrl}
            sr-context={this.errorMessageData.messageLinkText}
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => this.aglDsModalRef.closeModal()}
          >
            {this.errorMessageData.messageLinkText}
          </agl-ds-link>
          .
        </agl-ds-p>
      );
    }

    if (this.errorMessageData.errorTitle === 'Unable to connect you' || this.errorMessageData.errorTitle === 'Unable to connect internet') {
      return (
        <agl-ds-p class="error-modal__message">
          {this.errorMessageData.errorMessage} <agl-ds-text fontWeight="semibold">nbn</agl-ds-text>{' '}
          {this.errorMessageData.errorMessageExtended}
        </agl-ds-p>
      );
    }

    return <agl-ds-p class="error-modal__message">{this.errorMessageData.errorMessage}</agl-ds-p>;
  }

  private renderSecondMessage(): JSX.Element {
    if (this.errorMessageData.secondErrorMessage) {
      return <agl-ds-p class="error-modal__message error-modal__second-message">{this.errorMessageData.secondErrorMessage}</agl-ds-p>;
    }
    return null;
  }

  public render(): JSX.Element {
    const hasSecondButton = this.errorMessageData.secondButtonUrl && this.errorMessageData.secondButtonLabel;
    return (
      <agl-ds-modal
        ref={(el: any) => (this.aglDsModalRef = el)}
        appearance="centerscreen"
        animation-type="popup"
        // eslint-disable-next-line react/jsx-no-bind
        onModalClosed={() =>
          this.handleModalClosed(this.errorMessageData.forceRefresh, this.errorMessageData.buttonUrl === '/apps/confirm/?#/movers/address')
        }
      >
        <div slot="header">
          <agl-ds-h1 styled-as="title3" font="fontfamily02" bottom-margin="none">
            {this.errorMessageData.errorTitle}
          </agl-ds-h1>
        </div>
        <div slot="content" class={`error-modal__content ${!this.errorMessageData.showFooter ? 'error-modal__content-extra-padding' : ''}`}>
          <div class="error-modal__content-text-wrapper">
            {this.renderMessage()}
            {this.renderSecondMessage()}
            {this.renderContact()}
          </div>
          <div class="error-modal__content-image-wrapper">
            <agl-ds-illustration
              image-path={getAssetPath(
                `../assets/icon/${this.errorMessageData.icon === 'nbn' ? 'agl_illus_dig_broadband.svg' : 'agl_illus_dig_energy.svg'}`
              )}
              size="sm"
              bottom-margin="none"
            />
          </div>
        </div>
        {this.errorMessageData.showFooter && (
          <div slot="footer">
            <div class="error-modal__button-wrapper">
              <div
                class={`error-modal__first-button-container ${hasSecondButton ? 'error-modal__first-button-container--bottom-spacer' : ''}`}
              >
                <agl-ds-button
                  href={this.errorMessageData.buttonUrl}
                  type="secondary"
                  openNewWindow={this.errorMessageData.buttonOpenNewWindow}
                  onClick={this.errorMessageData.buttonUrl === '/apps/confirm/?#/movers/address' && this.clearNbnMoversStoredData}
                >
                  {this.errorMessageData.buttonLabel}
                </agl-ds-button>
              </div>
              {hasSecondButton && (
                <div class="error-modal__second-button-container">
                  <agl-ds-button
                    class="error-modal__second-button"
                    href={this.errorMessageData.secondButtonUrl}
                    type="secondary"
                    openNewWindow={this.errorMessageData.secondButtonOpenNewWindow}
                  >
                    {this.errorMessageData.secondButtonLabel}
                  </agl-ds-button>
                </div>
              )}
            </div>
          </div>
        )}
      </agl-ds-modal>
    );
  }
}
